import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { SharedModule } from '../../shared/shared.module';
import { TMSService } from '../../core/service/tms/tms.service';
import { TmsSocketService } from '../../core/service/tms-socket/tms-socket.service';
import { DomainConfigService } from '../../core/service/domainConfig/domain-config.service';
import { AuthenticationService } from '../../core/service/auth/authentication.service';
import { LocalStorageService } from '../../core/service/localStorage/local-storage.service';
import { LoaderService } from '../../core/service/loader.service';
import { PopupServiceService } from '../../core/service/popup/popup-service.service';
import { AwayStatusComponent } from '../../shared/away-status/away-status.component';
import { TodayOverviewDialogComponent } from '../../shared/today-overview-dialog/today-overview-dialog.component';
import { TodayOverviewService } from '../../shared/today-overview/today-overview.service';

@Component({
  selector: 'app-start-day',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  templateUrl: './start-day.component.html',
  styleUrl: './start-day.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class StartDayComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private domainConfigService = inject(DomainConfigService);
  private tmsService = inject(TMSService);
  private tmsSocketService = inject(TmsSocketService);
  private auth = inject(AuthenticationService);
  private localStorageService = inject(LocalStorageService);
  private loader = inject(LoaderService);
  private popup = inject(PopupServiceService);
  private todayOverviewService = inject(TodayOverviewService);

  brandLogo = './assets/images/logo.svg';
  buttonBgColor = '#FF6525';
  buttonTextColor = '#ffffff';
  companyName = 'Crizac Ltd';

  focusLine = '';
  businessAsUsual = false;
  readonly maxLength = 500;

  currentTime = '';
  currentDate = '';
  currentYear = moment().format('YYYY');

  // True when the user landed here because they forgot to sign off last time —
  // there was nothing to catch up on (no pending tasks/events), so the
  // sign-off-wrapup step was skipped and this note is shown instead.
  missedSignOff = false;

  private _clockInterval: any;

  constructor() {
    const nav = this.router.getCurrentNavigation();
    this.missedSignOff = !!nav?.extras?.state?.['missedSignOff'];
  }

  ngOnInit(): void {
    // Reconnect TMS socket on refresh (start-day has no header/layout)
    this.tmsSocketService.connect();

    this.brandLogo = this.domainConfigService.getLoginLogo();
    this.buttonBgColor = this.domainConfigService.getButtonBgColor();
    this.buttonTextColor = this.domainConfigService.getButtonTextColor();
    this.companyName = this.domainConfigService.getCompanyName();

    this._updateClock();
    this._clockInterval = setInterval(() => this._updateClock(), 1000);
  }

  ngOnDestroy(): void {
    clearInterval(this._clockInterval);
  }

  private _updateClock(): void {
    const now = moment();
    this.currentTime = now.format('hh:mm A');
    this.currentDate = now.format('dddd, MMMM D, YYYY');
  }

  async onStartDay(): Promise<void> {
    const currentUser = await this.localStorageService.getKeyIndexedDbKey('currentUser');

    const payload = {
      userId: currentUser?.id,
      businessAsUsual: this.businessAsUsual,
      startDayDescription: this.focusLine?.trim() || '',
      metadata: {},
    };

    this.loader.show();
    this.tmsService.signIn(payload).subscribe({
      next: () => {
        this.loader.hide();
        this.tmsService.markDayStarted();
        this.proceedAfterStartDay(currentUser);
      },
      error: (err: any) => {
        this.loader.hide();
        const msg = err?.error?.message || '';
        if (err?.status === 400 && msg === 'Already signed in today') {
          this.tmsService.markDayStarted();
          this.proceedAfterStartDay(currentUser);
          return;
        }
        this.popup.successAndErrorDialog('error', msg || 'Failed to start day. Please try again.');
      },
    });
  }

  private async proceedAfterStartDay(currentUser: any): Promise<void> {
    const redirectUrl = this.auth.getRedirectUrl();
    if (redirectUrl) {
      await this.router.navigateByUrl(redirectUrl);
    } else {
      await this.router.navigate(['/dashboard/all-application']);
    }

    // New users who haven't set an MPIN yet must set one after landing on the page
    if (currentUser?.isMpinAdded === false) {
      const mpinDialogRef = this.dialog.open(AwayStatusComponent, {
        panelClass: 'custom-dialog-container',
        width: '400px',
        disableClose: true,
        data: { context: 'login' },
      });
      // Wait for the mandatory MPIN setup to close before showing the recap —
      // both are disableClose-free/true respectively and shouldn't stack.
      mpinDialogRef.afterClosed().subscribe(() => this._openTodayOverview());
    } else {
      this._openTodayOverview();
    }
  }

  // Compact, read-only recap of today's tasks/calendar — shown every start-of-day
  // so the user has a quick reminder of what's on their plate. Only opens when
  // the API actually has something to show.
  private _openTodayOverview(): void {
    this.todayOverviewService.hasTodayData().subscribe(hasData => {
      if (!hasData) return;
      this.dialog.open(TodayOverviewDialogComponent, {
        panelClass: 'custom-dialog-container',
        width: '1100px',
        disableClose: false,
      });
    });
  }
}